/**
 * POS New Customer Form Component
 * Quick customer registration from POS
 */
import { useState } from 'react';
import { UserPlus, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

export default function POSNewCustomerForm({ onSave, onCancel }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    country_code: '+679'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    
    if (!formData.phone.trim() && !formData.email.trim()) {
      toast.error('Please enter phone or email');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/staff/contacts', {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim() ? `${formData.country_code}${formData.phone.trim()}` : null,
        email: formData.email.trim() || null,
        source: 'pos'
      });
      
      toast.success('Customer created!');
      onSave(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6" data-testid="pos-new-customer-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onCancel} className="text-gray-600 dark:text-gray-300">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Customer</h1>
          <p className="text-gray-500 dark:text-gray-400">Quick registration from POS</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-md mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm">
          <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserPlus className="w-8 h-8 text-teal-600" />
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name *</label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  className="h-12"
                  data-testid="pos-new-customer-firstname"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                  className="h-12"
                  data-testid="pos-new-customer-lastname"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <div className="flex gap-2">
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, country_code: e.target.value }))}
                  className="h-12 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="+679">+679 (Fiji)</option>
                  <option value="+61">+61 (AU)</option>
                  <option value="+64">+64 (NZ)</option>
                  <option value="+1">+1 (US)</option>
                </select>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="1234567"
                  className="h-12 flex-1"
                  data-testid="pos-new-customer-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="h-12"
                data-testid="pos-new-customer-email"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 h-12 bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="pos-save-customer-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save & Continue'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
